import { useMemo, useState } from 'react';
import { Plus, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { addContact, removeContact, type Contact } from '@/lib/api/contacts';
import { setRecipients } from '@/lib/api/monitoring';
import { normalizePhone } from '@/lib/phone';

type Props = {
  recipients: string[];
  contacts: Contact[] | undefined;
  label: (number: string) => string;
};

export function RecipientsTab({ recipients, contacts, label }: Props) {
  const [newRecipient, setNewRecipient] = useState('');
  const [contactPick, setContactPick] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactNumber, setContactNumber] = useState('');

  // Contacts not already on the recipient list.
  const addableContacts = useMemo(
    () =>
      (contacts ?? [])
        .filter((c) => c.number && !recipients.includes(c.number))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [contacts, recipients]
  );

  const addRecipient = () => {
    const value = normalizePhone(newRecipient);
    if (value.length < 4 || recipients.includes(value)) return;
    setRecipients([...recipients, value]);
    setNewRecipient('');
  };

  const removeRecipient = (value: string) =>
    setRecipients(recipients.filter((r) => r !== value));

  const onPickContact = (number: string) => {
    if (number && !recipients.includes(number)) {
      setRecipients([...recipients, number]);
    }
    setContactPick('');
  };

  const onAddContact = () => {
    const name = contactName.trim();
    const number = contactNumber.trim();
    if (!name || !number) return;
    addContact(name, number);
    setContactName('');
    setContactNumber('');
  };

  return (
    <div className="w-full">
      {/* Recipients */}
      <div className="rounded-md border p-4">
        <p className="font-medium">Alert recipients</p>
        <p className="mb-3 text-sm text-muted-foreground">
          Pick from contacts, or add a number in E.164 format (e.g.
          +15145551234).
        </p>
        <div className="mb-3 flex flex-wrap gap-2">
          {recipients.length === 0 && (
            <span className="text-sm text-muted-foreground">
              No recipients — alerts won&apos;t be sent.
            </span>
          )}
          {recipients.map((r) => (
            <Badge key={r} variant="secondary" className="gap-1">
              {label(r)}
              <button
                type="button"
                aria-label={`Remove ${r}`}
                onClick={() => removeRecipient(r)}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={contactPick} onValueChange={onPickContact}>
            <SelectTrigger className="w-[240px]">
              <SelectValue placeholder="Add from contacts…" />
            </SelectTrigger>
            <SelectContent>
              {addableContacts.length === 0 ? (
                <SelectItem value="__none" disabled>
                  No contacts available
                </SelectItem>
              ) : (
                addableContacts.map((c) => (
                  <SelectItem key={c.id} value={c.number}>
                    {c.name} ({c.number})
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
          <span className="text-sm text-muted-foreground">or</span>
          <Input
            placeholder="+15145551234"
            value={newRecipient}
            onChange={(e) => setNewRecipient(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addRecipient();
              }
            }}
            className="max-w-xs"
          />
          <Button variant="outline" onClick={addRecipient}>
            <Plus className="mr-1 h-4 w-4" />
            Add
          </Button>
        </div>
      </div>

      {/* Contacts directory */}
      <div className="mt-6 rounded-md border p-4">
        <p className="font-medium">Contacts</p>
        <p className="mb-3 text-sm text-muted-foreground">
          Saved people you can quickly add as recipients above.
        </p>
        <div className="mb-3 flex flex-wrap gap-2">
          {(contacts ?? []).length === 0 && (
            <span className="text-sm text-muted-foreground">
              No contacts yet.
            </span>
          )}
          {(contacts ?? [])
            .slice()
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((c) => (
              <Badge key={c.id} variant="outline" className="gap-1">
                {c.name} · {c.number}
                <button
                  type="button"
                  aria-label={`Remove ${c.name}`}
                  onClick={() => c.id && removeContact(c.id)}
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Input
            placeholder="Name"
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            className="max-w-[180px]"
          />
          <Input
            placeholder="+15145551234"
            value={contactNumber}
            onChange={(e) => setContactNumber(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                onAddContact();
              }
            }}
            className="max-w-[200px]"
          />
          <Button variant="outline" onClick={onAddContact}>
            <Plus className="mr-1 h-4 w-4" />
            Add contact
          </Button>
        </div>
      </div>
    </div>
  );
}
